import Check from "@/assets/check.svg";
import Cup from "@/assets/cup.svg";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Headset, Loader2, Mail, ReceiptText, Zap } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button.styles";
import { useMe } from "@/context/user-context";
import { useToast } from "@/hooks/use-toast";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn, formatPrice } from "@/lib/utils";

const CONTACT_MAILTO = "mailto:jules@teamz-tennis.com";

// Nouvelle grille : Freemium (carte statique), Premium (seul plan Stripe vendu,
// 275 €/an) et Premium piloté (à partir de 575 €/an, facturé hors application).
// Ce prix plancher n'existe pas côté Stripe : le plan est vendu et facturé par
// l'équipe commerciale, d'où la constante ci-dessous plutôt qu'un tarif chargé.
const PREMIUM_MANAGED_FROM_PRICE_CENTS = 57_500;

const FREEMIUM_ARGS = ["common:plan.freemium-arg-1", "common:plan.freemium-arg-2", "common:plan.freemium-arg-3"];
const PREMIUM_ARGS = ["common:plan.premium-arg-1", "common:plan.premium-arg-2", "common:plan.premium-arg-3"];
const PREMIUM_MANAGED_ARGS = ["common:plan.premium-managed-arg-1", "common:plan.premium-managed-arg-2", "common:plan.premium-managed-arg-3"];

const cardClasses = "relative mx-auto flex w-full max-w-sm flex-col justify-between gap-8 rounded-2xl border p-6 transition-colors lg:max-w-none";

function PlanArgs({ args }: { args: string[] }) {
  const { t } = useTranslation(["common"]);

  return (
    <div className="flex flex-col gap-4 p-2">
      {args.map(argKey => (
        <div key={argKey} className="flex items-start gap-2">
          <Check className="mt-1 size-4 shrink-0" />
          <span className="text-sm leading-[125%] text-neutral-600">
            {tDyn(t, argKey)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function PlansPage() {
  const { t, i18n } = useTranslation(["common"]);
  const { user } = useMe();
  const trpc = useTRPC();
  const { data, status } = useQuery(trpc.payment.getPlanPrices.queryOptions());
  const { data: playersStats } = useQuery(trpc.player.getPlayersStats.queryOptions());
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const { toast } = useToast();

  const { mutate: createBillingPortalSession } = useMutation(trpc.payment.createBillingPortalSession.mutationOptions({
    onSuccess: (data) => {
      if (data) {
        globalThis.window.location.replace(data);
      }
    },
    onError: () => {
      toast({
        title: tDyn(t, "common:error-occured"),
        description: "Une erreur est survenue, veuillez réessayer",
      });
    },
  }));

  const handleCreateBillingPortalSession = () => {
    if (!user.stripeId)
      return;
    createBillingPortalSession({
      urlPath: window.location.pathname,
    });
  };

  const { mutate } = useMutation(trpc.payment.createCheckoutSession.mutationOptions({
    onSuccess: (data) => {
      setLoadingPlan(null);
      if (data) {
        globalThis.window.location.replace(data);
      }
    },
    onError: () => {
      setLoadingPlan(null);
    },
  }));

  if (status === "pending") {
    return <Loader2 className="mx-auto mt-10 size-8 animate-spin text-orange-500" />;
  }
  if (status === "error") {
    throw new Error("Failed to fetch plans");
  }

  const premium = data.premium;
  // Les clubs encore sur un pack_* sont abonnés : ils voient « Gérer », pas
  // « Acheter », et leur abonnement n'est pas modifié par cette page.
  const hasLegacyPack = Boolean(user.plan) && user.plan !== "premium";

  return (
    <div className="container mx-auto mb-8 flex flex-col gap-8 px-4 py-8">
      <div className="flex flex-col items-center justify-between gap-4 lg:flex-row">
        <h2 className="text-center text-2xl font-bold">{tDyn(t, "common:plan.plans-title")}</h2>
        <Button
          variant="outline"
          onClick={() => {
            setLoadingPlan("invoices");
            handleCreateBillingPortalSession();
          }}
          loading={loadingPlan === "invoices"}
          className="min-w-32"
          disabled={!user.stripeId}
        >
          <ReceiptText strokeWidth={1.5} className="size-5" />
          {tDyn(t, "common:plan.invoices")}
        </Button>
      </div>

      <div className="flex flex-col gap-4">
        <div className="grid gap-4 lg:grid-cols-3">
          <div className={cn(cardClasses, "border-neutral-300 bg-white")}>
            {!user.plan && (
              <div className="absolute left-1/2 top-0 -translate-x-1/2 text-nowrap rounded-b-lg bg-orange-500 px-4 text-xs font-semibold text-orange-100">
                {tDyn(t, "common:plan.actual-plan")}
              </div>
            )}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="flex items-center justify-center rounded-xl bg-orange-100 p-2">
                    <Cup className="size-6 text-orange-500" />
                  </div>
                  <span className="text-xl font-semibold leading-[115%]">{tDyn(t, "common:plan.freemium")}</span>
                </div>
              </div>
              <PlanArgs args={FREEMIUM_ARGS} />
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex items-end justify-center gap-1 py-2">
                <span className="text-center text-2xl font-semibold leading-[115%]">
                  {formatPrice(0)}
                </span>
                <span className="text-sm text-neutral-500">
                  {tDyn(t, "common:plan.per-year")}
                </span>
              </div>
              <Button variant="outline" disabled>
                {!user.plan ? tDyn(t, "common:plan.actual-plan") : tDyn(t, "common:plan.freemium")}
              </Button>
              <div className="h-6" />
            </div>
          </div>

          <div className={cn(cardClasses, "border-orange-500 bg-orange-100/75")}>
            {user.plan === "premium" && (
              <div className="absolute left-1/2 top-0 -translate-x-1/2 text-nowrap rounded-b-lg bg-orange-500 px-4 text-xs font-semibold text-orange-100">
                {tDyn(t, "common:plan.actual-plan")}
              </div>
            )}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="flex items-center justify-center rounded-xl bg-orange-500 p-2">
                    <Zap strokeWidth={1.5} className="size-6 text-orange-100" />
                  </div>
                  <span className="text-xl font-semibold leading-[115%]">{tDyn(t, "common:plan.premium")}</span>
                </div>
              </div>
              <PlanArgs args={PREMIUM_ARGS} />
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex items-end justify-center gap-1 py-2">
                <span className="text-center text-2xl font-semibold leading-[115%]">
                  {formatPrice(premium.unit_amount)}
                </span>
                <span className="text-sm text-neutral-500">
                  {tDyn(t, "common:plan.per-year")}
                </span>
              </div>
              <Button
                onClick={() => {
                  setLoadingPlan("premium");
                  if (user.plan) {
                    handleCreateBillingPortalSession();
                    return;
                  }
                  // resolvedLanguage plutôt que language : c'est la valeur déjà
                  // ramenée à supportedLngs, donc celle que l'utilisateur voit.
                  mutate({ plan: "premium", urlPath: window.location.pathname, locale: i18n.resolvedLanguage });
                }}
                loading={loadingPlan === "premium"}
              >
                {user.plan ? tDyn(t, "common:manage") : tDyn(t, "common:plan.subscribe")}
              </Button>
              <div className="flex items-center justify-center gap-1.5">
                <span className="text-[22px] font-semibold text-orange-500">{playersStats?.allPlayersStat}</span>
                <span className="font-medium text-orange-500">{tDyn(t, "common:plan.players-waiting-for-you")}</span>
              </div>
            </div>
          </div>

          {/* Premium piloté : pas de checkout Stripe, l'offre est cadrée et
              facturée par l'équipe commerciale. */}
          <div className={cn(cardClasses, "border-neutral-300 bg-white")}>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="flex items-center justify-center rounded-xl bg-orange-100 p-2">
                    <Headset strokeWidth={1.5} className="size-6 text-orange-500" />
                  </div>
                  <span className="text-xl font-semibold leading-[115%]">{tDyn(t, "common:plan.premium-managed")}</span>
                </div>
              </div>
              <PlanArgs args={PREMIUM_MANAGED_ARGS} />
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex items-end justify-center gap-1 py-2">
                <span className="text-center text-2xl font-semibold leading-[115%]">
                  {t("common:plan.from-price", { price: formatPrice(PREMIUM_MANAGED_FROM_PRICE_CENTS) })}
                </span>
                <span className="text-sm text-neutral-500">
                  {tDyn(t, "common:plan.per-year")}
                </span>
              </div>
              <a href={CONTACT_MAILTO} className={cn(buttonVariants({ variant: "outline" }))}>
                {tDyn(t, "common:plan.ask-quote")}
                <Mail className="size-5" />
              </a>
              <div className="h-6" />
            </div>
          </div>
        </div>

        {hasLegacyPack && (
          <p className="text-center text-sm font-medium text-neutral-600">
            {tDyn(t, "common:plan.legacy-pack-notice")}
          </p>
        )}

        <p className="text-center font-medium text-neutral-600">
          {tDyn(t, "common:plan.plans-conditions")}
        </p>
      </div>

      <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
        <a
          href={CONTACT_MAILTO}
          className={cn(buttonVariants({ variant: "default" }))}
        >
          {tDyn(t, "common:plan.contact")}
          <Mail className="size-5" />
        </a>
      </div>
    </div>
  );
}
